import { useCallback, useEffect, useState } from 'react';
import { addNote, editNote, getNotes, type NoteTarget } from '@/features/operations/api';
import type { NoteEntry } from '@/features/operations/types';

/// `append` adds a note created elsewhere (e.g. by the WhatsApp contact) without a reload;
/// `reload` reads them again after an action that wrote one on the server (VEN-2).
export function useNotes(token: string | null, target: NoteTarget) {
  const [notes, setNotes] = useState<NoteEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const { type, id } = target;

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getNotes(token, { type, id })
      .then((loaded) => {
        if (!cancelled) {
          setNotes(loaded);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'No pudimos cargar las notas.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, type, id, version]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);

  const append = useCallback((note: NoteEntry) => {
    setNotes((current) => [...current, note]);
  }, []);

  const add = useCallback(
    async (body: string) => {
      if (!token) {
        return;
      }

      append(await addNote(token, { type, id }, body));
    },
    [token, type, id, append],
  );

  const edit = useCallback(
    async (noteId: string, body: string) => {
      if (!token) {
        return;
      }

      const updated = await editNote(token, noteId, body);
      setNotes((current) => current.map((note) => (note.id === updated.id ? updated : note)));
    },
    [token],
  );

  return { notes, isLoading, error, add, edit, append, reload };
}
